<app-sidenav #sidenav [showToggleButton]="profileActive" [initiallyOpen]="false">
  <!-- ── Header: profile selectors, patient picker, search ──────────────────── -->
  <ng-container sidenav-header>
    @if (!profileLoading) {
      <div [formGroup]="profileForm" class="profile-form">
        <mat-form-field [hideRequiredMarker]="true" appearance="outline" [subscriptSizing]="'dynamic'">
          <mat-label class="select-label">{{
            'epd.menu.selector.select-organization' | translate | capitalize
          }}</mat-label>
          <mat-select [formControlName]="'institutionControl'" panelClass="select-panel" [panelWidth]="null">
            @for (institution of this.careInstitutions; track institution) {
              <mat-option (click)="this.onInstitutionChange(institution)" [value]="institution.id">
                {{ institution.name }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field [hideRequiredMarker]="true" appearance="outline" [subscriptSizing]="'dynamic'">
          <mat-label class="select-label">{{
            'epd.menu.selector.select-department' | translate | capitalize
          }}</mat-label>
          <mat-select [formControlName]="'departmentControl'" panelClass="select-panel">
            @for (department of this.departments; track department) {
              <mat-option (click)="this.onDepartmentChange(department)" [value]="department.id">
                {{ department.name }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field [hideRequiredMarker]="true" appearance="outline" [subscriptSizing]="'dynamic'">
          <mat-label class="select-label">{{ 'epd.menu.selector.select-role' | translate | capitalize }}</mat-label>
          <mat-select [formControlName]="'departmentRoleControl'" panelClass="select-panel">
            @for (departmentRole of this.departmentRoles; track departmentRole) {
              <mat-option (click)="this.onDepartmentRoleChange(departmentRole)" [value]="departmentRole.id">
                {{ departmentRole.name }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
      <mat-divider></mat-divider>
      <!-- Patient picker button -->
      <div class="nav-list" style="padding-top: 0px !important">
        <div class="nav-list-item noselect" routerLinkActive="active" (click)="this.openPatientDrawer()">
          <div class="profile-wrapper">
            <div class="patient-name-wrapper">
              @if (!this.activePatient) {
                <span>
                  {{
                    'epd.menu.selector.select-patient'
                      | translate: { PATIENT_LABEL: this.activeCareInstitution?.patientTitleSingular }
                      | capitalize
                  }}
                </span>
              }
              @if (this.activePatient) {
                <span style="font-weight: 500">{{ this.activePatient.getPropertyValue('zoeknaam') }}</span>
              }
              <br />
              @if (this.activePatient) {
                <span>{{ this.activePatient.getPropertyValue('geboortedatum') | date: 'dd-MM-yyyy' }}</span>
              }
            </div>
            <div class="profile-icon-wrapper">
              @if (getImgUrl(); as url) {
                @if (url === 'icon') {
                  <mat-icon>account_box</mat-icon>
                }
                @if (url !== 'icon') {
                  <img defaultImage="/assets/images/loader1.gif" title="profile image" [lazyLoad]="url" />
                }
              }
            </div>
          </div>
        </div>
      </div>
    }

    @if (profileLoading) {
      <mat-spinner class="white-spinner" style="margin: 53px 72.5px" diameter="80"></mat-spinner>
    }

    @if (!this.activePatient) {
      <mat-divider></mat-divider>
    }

    <!-- Function search bar -->
    @if (this.searchedCategories && this.activePatient) {
      <div>
        <mat-form-field class="search-bar" appearance="outline" [subscriptSizing]="'dynamic'">
          <mat-icon class="search-icon" matPrefix>search</mat-icon>
          <input
            class="search-bar-input"
            autofocus
            type="text"
            matInput
            placeholder="{{ 'epd.menu.filter-functions-placeholder' | translate | capitalize }}"
            [(ngModel)]="this.searchQuery"
            (input)="filterNavItems($event)" />
          @if (this.searchQuery) {
            <mat-icon class="reset-search-query-button" matSuffix (click)="this.clearSearchQuery()">clear</mat-icon>
          }
        </mat-form-field>
      </div>
    }
  </ng-container>

  <!-- ── Nav: dashboard item + function accordion ──────────────────────────── -->
  <ng-container sidenav-nav>
    <app-nav-list
      [items]="dashboardNavItem"
      [groups]="navGroups"
      [groupsExpanded]="searching"
      [loading]="functionsLoading">
    </app-nav-list>
  </ng-container>

  <!-- ── Main page content ────────────────────────────────────────────────────── -->
  <router-outlet></router-outlet>
</app-sidenav>

<!-- ── Patient picker overlay drawer (EPD-specific, separate from the shared shell) -->
<mat-drawer-container class="patient-drawer-container" hasBackdrop="true">
  <mat-drawer #patientDrawer class="patient-drawer" mode="over" style="height: 90vh">
    <mat-card
      appearance="outlined"
      class="card-primary"
      [style.height]="patientDrawerSelectedToggle === 'all' ? '100%' : 'unset'">
      <button mat-icon-button class="close-button" (click)="closePatientDrawer()">
        <mat-icon>clear</mat-icon>
      </button>
      <div class="patient-list-wrapper" [class.patient-drawer-loading]="loading">
        @if (loading) {
          <mat-spinner style="margin: auto; margin-top: 100px"></mat-spinner>
        }
        @if (this.departmentPatients && !loading) {
          <app-list
            [shadow]="false"
            [title]="this.activeCareInstitution!.patientTitlePlural | titlecase"
            [columns]="patientDrawerColumns"
            [data]="this.departmentPatients"
            [pageSizeOptions]="[25, 50, 100]"
            [addButton]="false"
            [searchBar]="true"
            (rowClick)="onRowClick($event)"
            [toggleButton]="true"
            (toggleButtonChange)="onToggleButtonChange($event)"
            [toggleButtonData]="toggleButtonData"
            [disabled]="patientDrawerTableDisabeld">
          </app-list>
        }
      </div>
    </mat-card>
  </mat-drawer>
  <mat-drawer-content></mat-drawer-content>
</mat-drawer-container>

<!-- ── AI Chat floating button & overlay ─────────────────────────────────────── -->
@if (aiChatButtonVisible) {
  <div class="ai-chat-button">
    <button
      mat-fab
      color="primary"
      cdkOverlayOrigin
      #aiChatOverlayTrigger="cdkOverlayOrigin"
      (click)="openAiChat()"
      matTooltipPosition="left"
      [matTooltip]="
        aiChatOverlayOpen ? ('general.actions.close' | translate) : ('epd.ai-chat.button-tooltip' | translate)
      ">
      <mat-icon>{{ aiChatOverlayOpen ? 'close' : 'chat' }}</mat-icon>
    </button>
  </div>

  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="aiChatOverlayTrigger"
    [cdkConnectedOverlayOpen]="aiChatOverlayOpen">
    <div class="overlay-wrapper">
      <app-ai-chat-overlay [cases]="aiCases" [aiChatUrl]="aiChatUrl"></app-ai-chat-overlay>
    </div>
  </ng-template>
}
